import { createContext, useContext, useState } from "react";
import { FirebaseContext } from "./FirebaseContext";
import { SessionContext } from "../contexts/SessionContext";
import { addFileUseCase } from "../useCases/addFile";
import { updateFilesUseCase } from "../useCases/updateFiles";
import { updateFileUseCase } from "../useCases/updateFile";
import { buildProjectUseCase } from "../useCases/buildProject";
import { serveProjectUseCase } from "../useCases/serveProject";
import { stopServeUseCase } from "../useCases/stopServe";
import { updateAddFilesUseCase } from "../useCases/updateAddFiles";

export const useRemoteFileActions = () => {
  const { getFreshToken } = useContext(FirebaseContext);
  const { getEndpointUrl } = useContext(SessionContext);
  const [browserTab, setBrowserTab] = useState(null);

  const updateAddFiles = async (files) => {
    await updateAddFilesUseCase({ getFreshToken, getEndpointUrl, files });
  };

  const addFile = async (filePath, content) => {
    await addFileUseCase({
      getFreshToken,
      getEndpointUrl,
      filePath,
      content,
    });
  };

  const updateFile = async (filePath, content) => {
    await updateFileUseCase({
      getFreshToken,
      getEndpointUrl,
      filePath,
      content,
    });
  };

  const updateFiles = async (files) => {
    return updateFilesUseCase({ getFreshToken, getEndpointUrl, files });
  };

  const buildProject = (repoName) =>
    buildProjectUseCase({
      getFreshToken,
      getEndpointUrl,
      repoName,
    });

  const serveProject = (repoName) =>
    serveProjectUseCase({
      getFreshToken,
      getEndpointUrl,
      repoName,
      browserTab,
      setBrowserTab,
    });

  const stopServe = () =>
    stopServeUseCase({
      getFreshToken,
      getEndpointUrl,
      browserTab,
      setBrowserTab,
    });

  const fetchFilesFromRepo = async (repoName) => {
    try {
      const freshToken = await getFreshToken();
      const response = await fetch(
        getEndpointUrl() + `/getFilesFromRepo/${encodeURIComponent(repoName)}`,
        {
          headers: {
            Authorization: `Bearer ${freshToken}`,
          },
        }
      );
      if (!response.ok)
        throw new Error("Error fetching files from the repository.");
      const files = await response.json();
      return files.files;
    } catch (error) {
      console.error("Error fetching files from repo:", error);
      return [];
    }
  };

  const fetchRepos = async () => {
    try {
      const freshToken = await getFreshToken();
      const response = await fetch(getEndpointUrl() + "/getRepos", {
        headers: {
          Authorization: `Bearer ${freshToken}`,
        },
      });
      const repos = await response.json();
      return repos;
    } catch (error) {
      console.error("Error fetching the repos list:", error);
      return [];
    }
  };

  const getFile = async (filePath) => {
    try {
      const freshToken = await getFreshToken();
      const response = await fetch(
        getEndpointUrl() + `/getFile?filePath=${encodeURIComponent(filePath)}`,
        {
          headers: {
            Authorization: `Bearer ${freshToken}`,
          },
        }
      );
      const data = await response.text();
      return data;
    } catch (error) {
      console.error("Error fetching the file content:", error);
      return "";
    }
  };

  const downloadRepo = async (gitUrl) => {
    try {
      const freshToken = await getFreshToken();
      const response = await fetch(getEndpointUrl() + "/downloadRepo", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${freshToken}`,
        },
        body: JSON.stringify({ gitUrl }),
      });
      if (!response.ok) throw new Error("Error downloading the repository.");
      const result = await response.json();
      console.log("Repository downloaded successfully:", result);
      return result;
    } catch (error) {
      console.error("Error downloading repository:", error);
      return null;
    }
  };

  const commitChanges = async (repoName, commitMessage) => {
    try {
      const freshToken = await getFreshToken();
      const response = await fetch(getEndpointUrl() + "/commitChanges", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${freshToken}`,
        },
        body: JSON.stringify({ repoName, commitMessage }),
      });
      if (!response.ok) throw new Error("Error committing changes.");
      const result = await response.json();
      console.log("Changes committed successfully:", result);
      return result;
    } catch (error) {
      console.error("Error committing changes:", error);
      return null;
    }
  };

  const pushChanges = async (repoName) => {
    try {
      const freshToken = await getFreshToken();
      const response = await fetch(getEndpointUrl() + "/pushChanges", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${freshToken}`,
        },
        body: JSON.stringify({ repoName }),
      });
      if (!response.ok) throw new Error("Error pushing changes.");
      const result = await response.json();
      console.log("Changes pushed successfully:", result);
      return result;
    } catch (error) {
      console.error("Error pushing changes:", error);
      return null;
    }
  };

  const pullChanges = async (repoName) => {
    try {
      const freshToken = await getFreshToken();
      const response = await fetch(getEndpointUrl() + "/pullChanges", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${freshToken}`,
        },
        body: JSON.stringify({ repoName }),
      });
      if (!response.ok) throw new Error("Error pulling changes.");
      const result = await response.json();
      console.log("Changes pulled successfully:", result);
      return result;
    } catch (error) {
      console.error("Error pulling changes:", error);
      return null;
    }
  };

  const getGitChanges = async (repoName) => {
    try {
      const freshToken = await getFreshToken();
      const response = await fetch(
        getEndpointUrl() +
          `/getGitChanges?repoName=${encodeURIComponent(repoName)}`,
        {
          headers: {
            Authorization: `Bearer ${freshToken}`,
          },
        }
      );
      if (!response.ok) throw new Error("Error fetching git changes.");
      const data = await response.json();
      console.log("Git changes:", data.changes);
      return data.changes;
    } catch (error) {
      console.error("Error fetching git changes:", error);
      return [];
    }
  };

  const revertGitState = async (repoName, file) => {
    try {
      const freshToken = await getFreshToken();
      const response = await fetch(getEndpointUrl() + "/revertGitState", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${freshToken}`,
        },
        body: JSON.stringify({ repoName, file }),
      });
      if (!response.ok)
        throw new Error("Error reverting file to last committed state.");
      const result = await response.json();
      console.log("File reverted successfully:", result.message);
      return result.message;
    } catch (error) {
      console.error("Error reverting file:", error);
      return null;
    }
  };

  const rollbackRepo = async (repoName) => {
    console.log("Initiating rollback for repository:", repoName);
    try {
      const freshToken = await getFreshToken();
      const response = await fetch(getEndpointUrl() + "/rollbackRepo", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${freshToken}`,
        },
        body: JSON.stringify({ repoName }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Rollback failed with response:", errorText);
        throw new Error("Error rolling back repository.");
      }
      const result = await response.json();
      console.log("Repository rolled back successfully:", result.message);
      return result.message;
    } catch (error) {
      console.error("Error in rollbackRepo function:", error);
      return null;
    }
  };

  return {
    buildProject,
    serveProject,
    stopServe,
    fetchFilesFromRepo,
    fetchRepos,
    getFile,
    updateAddFiles,
    addFile,
    updateFile,
    updateFiles,
    downloadRepo,
    commitChanges,
    pushChanges,
    pullChanges,
    getGitChanges,
    revertGitState,
    rollbackRepo,
  };
};

const RemoteFileActionsContext = createContext(null);

export const RemoteFileActionsProvider = ({ children }) => {
  const remoteFileActions = useRemoteFileActions();

  return (
    <RemoteFileActionsContext.Provider value={remoteFileActions}>
      {children}
    </RemoteFileActionsContext.Provider>
  );
};

export const useRemoteFileActionsContext = () => {
  const context = useContext(RemoteFileActionsContext);
  if (!context) {
    throw new Error(
      "useRemoteFileActionsContext must be used within a RemoteFileActionsProvider"
    );
  }
  return context;
};
